import type { RisqPrepTiming } from './content';
import { effectiveTimings, PREP_TIMING_ORDER } from './prep';
import { isDone, percentOf, type RisqStatus } from './status';

/**
 * Vessel progress for the Master's phone screen and the dashboard (owner, 2026-11-03): by
 * position (done / left) and by preparation phase (% done). No sections. Done = Closed or Office
 * action.
 */

export interface ProgressCount {
  total: number;
  done: number;
  left: number;
  percentDone: number;
}

function count(statuses: readonly RisqStatus[]): ProgressCount {
  const done = statuses.filter(isDone).length;
  return {
    total: statuses.length,
    done,
    left: statuses.length - done,
    percentDone: percentOf(done, statuses.length),
  };
}

export interface PositionProgressItem {
  status: RisqStatus;
  /** Effective responsible positions; empty = unassigned. */
  positions: readonly string[];
}

/**
 * Progress per responsible position, in the order of `positionOrder`; a question shared by two
 * positions counts for both. `unassigned` counts questions without a position.
 */
export function progressByPosition(
  items: readonly PositionProgressItem[],
  positionOrder: readonly string[],
): { positions: { code: string; progress: ProgressCount }[]; unassigned: ProgressCount } {
  const byCode = new Map<string, RisqStatus[]>();
  const unassigned: RisqStatus[] = [];
  for (const item of items) {
    if (item.positions.length === 0) {
      unassigned.push(item.status);
    }
    for (const code of item.positions) {
      byCode.set(code, [...(byCode.get(code) ?? []), item.status]);
    }
  }
  const order = [
    ...positionOrder.filter((c) => byCode.has(c)),
    ...[...byCode.keys()].filter((c) => !positionOrder.includes(c)).sort(),
  ];
  return {
    positions: order.map((code) => ({ code, progress: count(byCode.get(code) ?? []) })),
    unassigned: count(unassigned),
  };
}

export interface PhaseProgressItem {
  status: RisqStatus;
  /** Timing flags as stored; no flag counts as 3-m. */
  prepTiming: readonly RisqPrepTiming[];
}

/** % done per preparation flag (6-m, 3-m, Last, C/L); a question with two flags counts in both. */
export function progressByPhase(
  items: readonly PhaseProgressItem[],
): { timing: RisqPrepTiming; progress: ProgressCount }[] {
  return PREP_TIMING_ORDER.map((timing) => ({
    timing,
    progress: count(
      items.filter((i) => effectiveTimings(i.prepTiming).includes(timing)).map((i) => i.status),
    ),
  }));
}
